import { motion, useScroll, useTransform } from "framer-motion";
import { heroDelay } from "../loadState";
import { HeroMedia } from "./HeroMedia";

const ease = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <section id="top" data-testid="hero" className="grain relative flex h-[100svh] min-h-[620px] items-end overflow-hidden bg-brand-brunswick">
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, delay: heroDelay(1.4), ease }}
      >
        <HeroMedia y={y} />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-licorice/75 via-brand-licorice/30 to-brand-licorice/20" />
      </motion.div>

      <div className="relative z-[2] mx-auto w-full max-w-7xl px-6 pb-24 sm:px-8 sm:pb-28 lg:pb-32">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: heroDelay(1.9), ease }}
            className="mb-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-champagne sm:text-xs"
          >
            Dune Villas · Area 83
          </motion.p>
          <h1 className="font-serif text-5xl font-light leading-[1.05] tracking-tight text-brand-ivory sm:text-6xl lg:text-7xl">
            <MaskedLine delay={heroDelay(2.0)}>Your private escape,</MaskedLine>
            <MaskedLine delay={heroDelay(2.14)} className="italic text-brand-champagne">
              just outside Bengaluru.
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: heroDelay(2.4), ease }}
            className="mt-7 max-w-xl text-[15px] leading-relaxed text-brand-ivory/85 sm:text-lg"
          >
            Made for unhurried stays, big celebrations and time well spent together.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: heroDelay(2.55), ease }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href="#enquire"
              data-testid="hero-enquire-cta"
              className="w-full bg-brand-rust px-9 py-4 text-center text-[13px] font-semibold uppercase tracking-[0.16em] text-brand-ivory transition-colors hover:bg-brand-sepia sm:w-auto"
            >
              Enquire about your stay
            </a>
            <a
              href="#villas"
              data-testid="hero-explore-cta"
              className="w-full border border-brand-ivory/50 px-9 py-4 text-center text-[13px] font-semibold uppercase tracking-[0.16em] text-brand-ivory transition-colors hover:bg-brand-ivory/10 sm:w-auto"
            >
              Explore the villas
            </a>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#intro"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to discover more"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: heroDelay(3), duration: 1 }}
        className="absolute bottom-8 right-6 z-[2] hidden flex-col items-center sm:right-8 sm:flex"
      >
        <span className="block text-[10px] font-medium uppercase tracking-[0.3em] text-brand-ivory/60">Scroll</span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="mt-3 block h-10 w-px origin-top bg-brand-ivory/50"
        />
      </motion.a>
    </section>
  );
};
